import React, { useState } from 'react';
import styled from 'styled-components/macro';
// eslint-disable-next-line no-restricted-imports
import { createStructuredSelector } from 'reselect';
import { RootState } from 'containers/PanesPanel/types';
import { Color } from 'lib/modelBuilder/entities/Color';
import { makeSelectColors } from 'containers/Configurator/selectors/colors';
import { useDispatch, useSelector } from 'react-redux';
import { updateColorPaneOnSelection } from 'containers/PanesPanel/actions';
import {
  ALUMINIUM_COLOR,
  BLACK_COLOR,
  BLACK_OPAQUE_COLOR,
  GLASS_COLOR,
  MIRROR_COLOR,
  NATURAL_COLOR,
  PaneMaterial,
  WHITE_COLOR,
  WHITE_OPAQUE_COLOR,
} from 'lib/modelBuilder/constants';
import { makeSelectLastHistoryCabinet } from 'containers/Configurator/selectors/history';
import { Cabinet } from 'lib/modelBuilder/entities/Cabinet';
import { restoreCabinet } from 'containers/Configurator/actions/furniture';
import ColorPickerList from 'components/ColorPickerList';
import { useTranslation } from 'react-i18next';
import { getMaterialColor } from 'lib/modelBuilder/utils/colorHelper';
import { setColorsPanelAction } from 'containers/Configurator/actions/general';
import { makeSelectCommonSelectionColor } from 'containers/PanesPanel/selectors';
import { PickerTitleCategory } from '../../../components/PickerTitleCategory';
import { makeSelectPaintColor } from '../../PaintPanel/selectors';
import { selectPaintColorAction } from '../../PaintPanel/actions';

interface Props {
  paintMode: boolean;
}

interface StateProps {
  colors: Color[];
  lastHistoryCabinet: Cabinet;
  commonColor: Color | undefined;
  paintColor: Color | undefined;
}

const stateSelector = createStructuredSelector<RootState, StateProps>({
  colors: makeSelectColors(),
  lastHistoryCabinet: makeSelectLastHistoryCabinet(),
  commonColor: makeSelectCommonSelectionColor(),
  paintColor: makeSelectPaintColor(),
});

const PanesColorPicker = ({ paintMode }: Props) => {
  const dispatch = useDispatch();
  const { colors, lastHistoryCabinet, commonColor, paintColor } = useSelector(
    stateSelector,
  );

  const [material, setMaterial] = useState(PaneMaterial.MDF);
  const { t } = useTranslation();

  const colorSelected = (
    material: PaneMaterial,
    color: Color,
    preview: boolean,
  ) => {
    if (paintMode) {
      dispatch(selectPaintColorAction(color));
    } else {
      dispatch(updateColorPaneOnSelection(material, color, preview));
    }
  };

  const onLeave = () => {
    // Restore the last history element
    dispatch(restoreCabinet(lastHistoryCabinet));
  };

  const filterColor = (color: Color) => {
    if (color.order >= 304) return false;
    if (material === PaneMaterial.MDF)
      return getMaterialColor(color) === PaneMaterial.MDF;
    else if (material === PaneMaterial.Frost)
      return getMaterialColor(color) === PaneMaterial.Frost;
    else if (material === PaneMaterial.FrostOpaque)
      return getMaterialColor(color) === PaneMaterial.FrostOpaque;
    else
      return (
        getMaterialColor(color) === PaneMaterial.Glass ||
        getMaterialColor(color) === PaneMaterial.Mirror ||
        getMaterialColor(color) === PaneMaterial.Alu
      );
  };

  const onClickColor = (color: Color) => {
    colorSelected(getMaterialColor(color), color, false);
  };

  const onMouseOverColor = (color: Color) => {
    colorSelected(getMaterialColor(color), color, true);
  };

  const onDoubleClick = () => {
    dispatch(setColorsPanelAction());
  };

  return (
    <div className="mx-2 px-1">
      <div className="flex bg-gray-200">
        <PickerTitleCategory
          selected={material === PaneMaterial.MDF}
          selectable
          onClick={() => {
            setMaterial(PaneMaterial.MDF);
          }}
        >
          {t('MDF')}
        </PickerTitleCategory>
        <PickerTitleCategory
          selected={material === PaneMaterial.Frost}
          selectable
          onClick={() => {
            setMaterial(PaneMaterial.Frost);
          }}
        >
          {t('Acrylic transparent')}
        </PickerTitleCategory>
        <PickerTitleCategory
          selected={material === PaneMaterial.FrostOpaque}
          selectable
          onClick={() => {
            setMaterial(PaneMaterial.FrostOpaque);
          }}
        >
          {t('Acrylic opaque')}
        </PickerTitleCategory>
        <PickerTitleCategory
          selected={
            material === PaneMaterial.Glass ||
            material === PaneMaterial.Mirror ||
            material === PaneMaterial.Alu
          }
          selectable
          onClick={() => {
            setMaterial(PaneMaterial.Alu);
          }}
        >
          {t('Others')}
        </PickerTitleCategory>
      </div>
      {colors && (
        <div onMouseLeave={onLeave}>
          <ColorPickerList
            colors={colors.filter(color => filterColor(color))}
            onClick={onClickColor}
            onMouseOver={!paintMode ? onMouseOverColor : null}
            onDoubleClick={!paintMode ? onDoubleClick : null}
            selectedColor={!paintMode ? commonColor : paintColor}
            bigColors={
              paintMode
                ? [
                    WHITE_COLOR,
                    NATURAL_COLOR,
                    BLACK_COLOR,
                    WHITE_OPAQUE_COLOR,
                    BLACK_OPAQUE_COLOR,
                    GLASS_COLOR,
                    MIRROR_COLOR,
                    ALUMINIUM_COLOR,
                  ]
                : undefined
            }
          />
        </div>
      )}
    </div>
  );
};

export default PanesColorPicker;

export const StyledColorIcon = styled.button<{ color: string }>`
  //h-8 w-8 bg-green mx-2 my-2
  height: 2.5rem;
  width: 2.5rem;
  margin: 0.5rem;
  background-color: ${p => p.color};
  border: solid 1px #e2e8f0;
  //box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06);
`;
